// Pre-mount prefetch for the member pages (OMG-1836, second step after
// routeChunks.ts moved the page chunk and the Contentful entry off the end
// of the waterfall).
//
// What was left on the critical path once those overlapped: React mounts,
// AuthenticationProvider resolves the Amplify session, NmhProvider and
// PageLayout then fire four omg-api requests (customers/me, user-preferences,
// experiments/member, media/cloudfront-cookies) which on UAT took 250 to
// 660 ms each, and only when they settle does the page render and discover
// its hero image. Measured on UAT after #1044: the requests started at
// ~1070 ms and the hero image at ~1865 ms, with the entry chunk done at 640.
//
// Two things happen here, both from main.tsx at module scope:
//
// 1. prefetchMemberSession: read the Amplify session (localStorage, no
//    network unless the token needs a refresh) and run the same four
//    queries through react-query with the same keys and fetchers the
//    providers use, so by the time they mount the data is cached or at
//    least in flight. react-query dedupes an in-flight query by key.
// 2. preloadHeroForPath: once the member-home Contentful entry is in the
//    cache, build the hero image URL exactly as the page will and add a
//    <link rel="preload"> for it, so the LCP image downloads while the API
//    requests are still pending instead of after the first render.
//
// Nothing here may block or break the mount: every promise is detached and
// every failure swallowed. Not signed in? Auth.currentAuthenticatedUser
// rejects, nothing is prefetched, AuthenticationProvider redirects as it
// always did.

import { Cookies } from "react-cookie";
import { COOKIE_NAMES } from "@constants/cookies";
import type {
  Article,
  NewMemberHomeData,
  TypeContentfulImage,
} from "@types.ts";

import { fetchUserData } from "@lib/api/customerApi";
import { fetchMemberExperiments } from "@lib/api/experimentsApi";
import { getCloudFrontCookies } from "@lib/api/mediaApi";
import { fetchUserPreferences } from "@lib/api/userPreferencesApi";
import { Auth } from "@lib/authentication/amplify/amplify";
import { parseAuthResult } from "@lib/authentication/amplify/amplify.helpers";
import { authenticationQueryKey } from "@lib/authentication/queries/authentication-query-keys";
import { contentfulImage } from "@lib/contentfulImage";
import {
  I18N_LOCALE_LNG_KEY,
  resolveInitialLocale,
} from "@lib/contexts/LocaleContext";
import { selectImageFromContentfulImages } from "@lib/helpers";
import { getUserPreferencesQueryKey } from "@lib/hooks/useGetUserPreferences";
import { MEMBER_ROUTES } from "@lib/routePaths";
import { safeLocalStorage } from "@lib/safeStorage";

import {
  isContentfulRoute,
  prefetchMemberHomeEntry,
  stripMembersBasename,
} from "@/routeChunks";
import { queryClient } from "@/utils/QueryClientProvider";

/**
 * Warm the four member queries. Keys, fetchers and the options that affect
 * caching mirror NmhContext.tsx (getMe, user-preferences, experiments) and
 * useGetCloudFrontCookies.ts; a drift there makes this a harmless extra
 * request, not a bug, but keep them in sync.
 */
export const prefetchMemberSession = (pathname: string): void => {
  if (!isContentfulRoute(pathname)) return;
  void (async () => {
    try {
      const { authenticatedUser, accessToken } = parseAuthResult(
        await Auth.currentAuthenticatedUser(),
      );
      const sub = authenticatedUser?.sub;
      const jwt = accessToken?.jwt;
      if (!sub || !jwt) return;

      void queryClient.prefetchQuery({
        queryKey: ["customers", "me", sub],
        queryFn: () => fetchUserData(sub),
        retry: false,
        networkMode: "always",
      });
      void queryClient.prefetchQuery({
        queryKey: getUserPreferencesQueryKey(sub),
        queryFn: fetchUserPreferences,
        retry: false,
      });
      void queryClient.prefetchQuery({
        queryKey: authenticationQueryKey.cloudFrontAuth(),
        queryFn: () => getCloudFrontCookies(jwt),
      });

      // The experiments key carries the locale LocaleProvider settles on at
      // mount; resolve it from the same three stores, in the same order.
      const locale = resolveInitialLocale({
        queryLng: new URLSearchParams(window.location.search).get("lng"),
        cookieLng: new Cookies().get(COOKIE_NAMES.LOCALE),
        storedLng: safeLocalStorage.getItem(I18N_LOCALE_LNG_KEY),
      });
      void queryClient.prefetchQuery({
        queryKey: ["experiments", "member", sub, locale],
        queryFn: () => fetchMemberExperiments(locale),
        staleTime: Infinity,
        networkMode: "always",
      });
    } catch {
      // Not signed in, or Amplify not ready: the providers handle it.
    }
  })();
};

type HeroVariant = "home" | "collection" | "article";

/**
 * The <picture> each page renders for its hero, as preload candidates. The
 * widths and breakpoints must match the page markup (NewMemberHome.tsx,
 * CollectionPage.tsx, ArticlePage.tsx) byte for byte, otherwise the browser
 * downloads the preloaded URL and then the page's own, different one.
 */
export const heroPreloadLinks = (
  images: TypeContentfulImage[] | undefined,
  variant: HeroVariant,
): { href: string; media: string }[] => {
  if (!images?.length) return [];
  const pick = (maxSize: number, w: number) =>
    contentfulImage(
      selectImageFromContentfulImages(images, maxSize)?.fields.file.url,
      { w },
    );
  const candidates =
    variant === "article"
      ? [
          { href: pick(360, 1536), media: "(max-width: 1023.98px)" },
          { href: pick(1024, 2880), media: "(min-width: 1024px)" },
        ]
      : [
          { href: pick(360, 768), media: "(max-width: 767.98px)" },
          { href: pick(768, 1024), media: "(min-width: 768px)" },
        ];
  return candidates.filter((c): c is { href: string; media: string } =>
    Boolean(c.href),
  );
};

const addPreload = ({ href, media }: { href: string; media: string }) => {
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = href;
  link.media = media;
  link.setAttribute("fetchpriority", "high");
  document.head.appendChild(link);
};

const under = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

const routeBase = (route: string): string => route.split("/:")[0];

const findArticleId = (
  data: NewMemberHomeData,
  slug: string,
  kebabCase: (value: string) => string,
): string | undefined => {
  const articles = data.fields?.articles as Article[] | undefined;
  return articles?.find(
    (a) =>
      a.fields.slug === slug || kebabCase(a.fields.articleHeading) === slug,
  )?.sys.id;
};

/**
 * Preload the hero image of /home, /collection/:id and /article/:id as soon
 * as the data that names it is in the cache. Home and collection read it off
 * the member-home entry; article needs its own entry, found by slug the way
 * ContentfulContext.getArticleIdBySlug does and fetched with the same query
 * ArticlePage runs, so that one is cached for the page as well.
 */
export const preloadHeroForPath = (pathname: string): void => {
  const p = stripMembersBasename(pathname);
  let variant: HeroVariant | undefined;
  if (under(p, MEMBER_ROUTES.HOME)) variant = "home";
  else if (under(p, routeBase(MEMBER_ROUTES.COLLECTION)))
    variant = "collection";
  else if (under(p, routeBase(MEMBER_ROUTES.ARTICLE))) variant = "article";
  if (!variant) return;

  void (async () => {
    try {
      const home = await prefetchMemberHomeEntry();
      if (!home) return;

      let images: TypeContentfulImage[] | undefined;
      if (variant === "home") {
        images = home.fields?.headerImages;
      } else if (variant === "collection") {
        images = home.fields?.collectionsHeaderImages;
      } else {
        const slug = p.slice(routeBase(MEMBER_ROUTES.ARTICLE).length + 1);
        // The per-method entry, not the package root: a dynamic import of
        // "lodash-es" is a namespace import Rollup cannot tree-shake, and
        // it grew the shared lodash-es chunk from 9 KB to 32 KB gzip.
        const [{ default: kebabCase }, { getContentfulGetEntryQueryOptions }] =
          await Promise.all([
            import("lodash-es/kebabCase"),
            import("@lib/helpers/getContentFulEntryQueryOptions"),
          ]);
        const articleId = findArticleId(home, slug, kebabCase);
        if (!articleId) return;
        const options = getContentfulGetEntryQueryOptions(articleId, {
          include: 4,
        });
        await queryClient.prefetchQuery(options);
        const article = queryClient.getQueryData(options.queryKey) as
          | { fields?: { headerImages?: TypeContentfulImage[] } }
          | undefined;
        images = article?.fields?.headerImages;
      }

      heroPreloadLinks(images, variant).forEach(addPreload);
    } catch {
      // Best effort: the page discovers its hero the usual way.
    }
  })();
};
